/**
 * Full-screen lock overlay for the installed PWA. Shown instead of the app when
 * locked, so no data loads behind it. Unlock = a WebAuthn platform-authenticator
 * gesture (Face ID / Touch ID / passcode). First time on a device registers the
 * credential (one tap). Fallback: sign out + back in via Google SSO.
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Logo } from '../components/Logo';
import { hasRegisteredCredential, registerAppLock, verifyAppLock } from './appLock';

export function LockScreen({
  email,
  onUnlocked,
  onSignOut,
}: {
  email: string;
  onUnlocked: () => void;
  onSignOut: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const firstTime = !hasRegisteredCredential();

  async function unlock() {
    setError(null);
    setBusy(true);
    try {
      const ok = firstTime ? await registerAppLock(email) : await verifyAppLock();
      if (ok) onUnlocked();
      else setError('Could not verify. Please try again.');
    } catch {
      // NotAllowedError (cancelled/failed) or an unsupported-authenticator error.
      setError('Unlock failed or was cancelled. Try again, or sign out.');
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-ink-0 px-6"
         style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div className="w-full max-w-sm text-center">
        <div className="flex justify-center mb-8">
          <Logo className="h-10" />
        </div>
        <svg viewBox="0 0 24 24" fill="none" className="w-12 h-12 mx-auto mb-4 text-accent" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
        </svg>
        <h1 className="font-display tracking-tightest text-white text-xl mb-1">App locked</h1>
        <p className="text-sm text-ink-400 mb-8">
          {firstTime
            ? 'Set up device unlock (Face ID, Touch ID, or your passcode) to protect your data.'
            : 'Unlock with Face ID, Touch ID, or your device passcode to continue.'}
        </p>

        <button
          type="button"
          onClick={() => void unlock()}
          disabled={busy}
          className="w-full btn-primary py-3 disabled:opacity-50"
        >
          {busy ? 'Waiting…' : firstTime ? 'Set up app lock' : 'Unlock'}
        </button>

        {error && <p className="text-sm text-accent mt-4">{error}</p>}

        <button
          type="button"
          onClick={onSignOut}
          className="mt-6 text-xs text-ink-500 hover:text-white"
        >
          Can’t unlock? Sign out
        </button>
        <p className="mt-6 text-[11px] text-ink-600 truncate">{email}</p>
      </div>
    </div>,
    document.body,
  );
}
